// src/pages/PublicCatalog/components/CartDialog.js
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Box,
    Button,
    Paper,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    cartDialog: {
        '& .MuiDialog-paper': {
            maxWidth: 600,
            width: '100%',
            margin: theme.spacing(2),
        },
    },
    cartItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
    },
    productInfo: {
        flex: 1,
        marginRight: theme.spacing(2),
    },
    quantityControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    quantity: {
        minWidth: '40px',
        textAlign: 'center',
    },
    total: {
        padding: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
    },
    emptyCart: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
    },
}));

const CartDialog = ({ 
    open, 
    onClose, 
    cart, 
    products, 
    onUpdateQuantity, 
    onRemoveItem, 
    onFinalize 
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const getTotal = () => {
        // Group totals by currency
        const totalsByCurrency = Object.entries(cart).reduce((acc, [productId, quantity]) => {
            const product = products.find(p => p.id === parseInt(productId));
            if (!product) return acc;
            
            const currency = product.currency;
            if (!acc[currency]) {
                acc[currency] = 0;
            }
            acc[currency] += product.price * quantity;
            return acc;
        }, {});

        // Return the formatted totals
        return Object.entries(totalsByCurrency).map(([currency, amount]) => (
            new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: currency
            }).format(amount)
        )).join(' + ');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.cartDialog}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Carrito de Compras</Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {Object.keys(cart).length === 0 ? (
                    <div className={classes.emptyCart}>
                        <Typography variant="body1" color="textSecondary">
                            El carrito está vacío
                        </Typography>
                    </div>
                ) : (
                    <>
                        {Object.entries(cart).map(([productId, quantity]) => {
                            const product = products.find(p => p.id === parseInt(productId));
                            if (!product) return null;

                            return (
                                <Paper key={productId} className={classes.cartItem} variant="outlined">
                                    <div className={classes.productInfo}>
                                        <Typography variant="subtitle1">
                                            {product.name}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {new Intl.NumberFormat('es-ES', {
                                                style: 'currency',
                                                currency: product.currency
                                            }).format(product.price)} × {quantity} = {
                                            new Intl.NumberFormat('es-ES', {
                                                style: 'currency',
                                                currency: product.currency
                                            }).format(product.price * quantity)}
                                        </Typography>
                                    </div>
                                    <div className={classes.quantityControls}>
                                        <IconButton 
                                            size="small"
                                            onClick={() => onUpdateQuantity(productId, -1)}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                        <Typography className={classes.quantity}>
                                            {quantity}
                                        </Typography>
                                        <IconButton 
                                            size="small"
                                            onClick={() => onUpdateQuantity(productId, 1)}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            className={classes.deleteButton}
                                            onClick={() => onRemoveItem(productId)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                </Paper>
                            );
                        })}
                        <Paper className={classes.total} variant="outlined">
                            <Typography variant="h6" align="right">
                                Total: {getTotal()}
                            </Typography>
                        </Paper>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onFinalize}
                    disabled={Object.keys(cart).length === 0}
                    fullWidth
                >
                    Finalizar Compra
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CartDialog;